import { Document, Page, Image as PdfImage, StyleSheet, Text, View } from "@react-pdf/renderer";
import logoComposite from "public/logo-composite.png";
import { useMemo } from "react";
import { ISocialMedia, Talent } from "@/api/talent/models";
import { ImportedPhotoData } from "@/components/features/talent/modals/PhotoImportModal";
import { ShoeSizeEnum } from "@/enums/measurements";
import { getShortName } from "@/utils/features/talent/names";

interface CompositeFormValues {
  template: string;
  photo1?: ImportedPhotoData | string | null;
  photo2?: ImportedPhotoData | string | null;
  photo3?: ImportedPhotoData | string | null;
  photo4?: ImportedPhotoData | string | null;
}

const getImageUrl = (photo: ImportedPhotoData | string | null | undefined): string => {
  if (!photo) {
    return "";
  }
  if (typeof photo === "string") {
    return photo;
  }
  return photo.url || "";
};

const pdfStyles = StyleSheet.create({
  page: {
    padding: 15,
    backgroundColor: "#FFFFFF",
  },
  image: {
    objectFit: "cover",
    width: "100%",
    height: "100%",
  },
  mainContainer: {
    flexDirection: "row",
    height: "100%",
    width: "100%",
  },
  leftColumn: {
    width: "35%",
    height: "100%",
  },
  rightColumn: {
    width: "65%",
    height: "100%",
    flexDirection: "column",
    paddingLeft: 15,
  },
  photosTopRow: {
    flexDirection: "row",
    height: "85%",
    gap: 15,
  },
  photoBox: {
    flex: 1,
    height: "100%",
  },
  footerInfoContainer: {
    height: "10%",
    width: "100%",
    flexDirection: "column",
    justifyContent: "flex-start",
    paddingTop: 8,
  },
  footerAgencyContainer: {
    height: "5%",
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
  },
  nameChild: {
    fontSize: 22,
    fontFamily: "Times-Bold",
    color: "#000000",
  },
  birthdayChild: {
    fontSize: 10,
    color: "#666666",
    fontFamily: "Helvetica",
    marginTop: 2,
    marginLeft: 1,
  },
  agencyInfoNew: {
    fontSize: 8,
    color: "#999999",
    letterSpacing: 1,
  },
  containerAdult: { flexDirection: "row", height: "85%", gap: 15 },
  adultColLarge: { flex: 2, height: "100%" },
  adultColSmallContainer: { flex: 1, height: "100%", flexDirection: "column", gap: 15 },
  adultColSmall: { flex: 1, width: "100%" },
  footerAdult: {
    position: "absolute",
    bottom: 25,
    left: 30,
    right: 30,
    flexDirection: "row",
    alignItems: "flex-end",
  },
  footerAdultLeft: { width: "35%", flexDirection: "column" },
  footerAdultCenter: {
    width: "45%",
    alignItems: "center",
  },
  name: { fontSize: 24, fontFamily: "Times-Bold" },
  instagram: { fontSize: 10, color: "#666666", marginLeft: 2 },
  measurements: { fontSize: 10, color: "#666666" },

  absoluteLogo: {
    position: "absolute",
    bottom: 25,
    right: 32,
    width: 45,
  },
});

export const CompositePdf = ({
  data,
  talent,
  isAdultLayout,
}: {
  data: CompositeFormValues;
  talent: Talent;
  isAdultLayout: boolean;
}) => {
  return useMemo(() => {
    const instagram =
      talent?.socialMedias?.find((social: ISocialMedia) => social?.plataform?.toUpperCase() === "INSTAGRAM")
        ?.username || "@agenciavogue";

    const height = talent?.attribute?.height ? `${talent.attribute.height}` : "--";
    const shoeSize = talent.attribute?.shoeSize?.map((size) => ShoeSizeEnum[size]).join(" --") || "-";
    const clothingSize = talent?.attribute?.clothingSize?.[0] || "--";

    const url1 = getImageUrl(data.photo1);
    const url2 = getImageUrl(data.photo2);
    const url3 = getImageUrl(data.photo3);
    const url4 = getImageUrl(data.photo4);

    return (
      <Document>
        <Page size="A4" orientation="landscape" style={pdfStyles.page}>
          {!isAdultLayout ? (
            <View style={pdfStyles.mainContainer}>
              <View style={pdfStyles.leftColumn}>{url1 && <PdfImage src={url1} style={pdfStyles.image} />}</View>

              <View style={pdfStyles.rightColumn}>
                <View style={pdfStyles.photosTopRow}>
                  <View style={pdfStyles.photoBox}>{url2 && <PdfImage src={url2} style={pdfStyles.image} />}</View>
                  <View style={pdfStyles.photoBox}>{url3 && <PdfImage src={url3} style={pdfStyles.image} />}</View>
                </View>

                <View style={pdfStyles.footerInfoContainer}>
                  <Text style={pdfStyles.nameChild}>{getShortName(talent?.fullName)}</Text>
                  {talent?.birthday && (
                    <Text style={pdfStyles.birthdayChild}>{new Date(talent.birthday).toLocaleDateString("pt-BR")}</Text>
                  )}
                </View>

                <View style={pdfStyles.footerAgencyContainer}>
                  <Text style={pdfStyles.agencyInfoNew}>@agenciavogue - 011-3032-9797</Text>
                </View>
              </View>
            </View>
          ) : (
            <>
              <View style={pdfStyles.containerAdult}>
                <View style={pdfStyles.adultColLarge}>{url1 && <PdfImage src={url1} style={pdfStyles.image} />}</View>
                <View style={pdfStyles.adultColLarge}>{url2 && <PdfImage src={url2} style={pdfStyles.image} />}</View>
                <View style={pdfStyles.adultColSmallContainer}>
                  <View style={pdfStyles.adultColSmall}>{url3 && <PdfImage src={url3} style={pdfStyles.image} />}</View>
                  <View style={pdfStyles.adultColSmall}>{url4 && <PdfImage src={url4} style={pdfStyles.image} />}</View>
                </View>
              </View>

              <View style={pdfStyles.footerAdult}>
                <View style={pdfStyles.footerAdultLeft}>
                  <Text style={pdfStyles.name}>{getShortName(talent?.fullName)}</Text>
                  <Text style={pdfStyles.instagram}>{instagram}</Text>
                </View>
                <View style={pdfStyles.footerAdultCenter}>
                  <Text style={pdfStyles.measurements}>
                    Altura {height} Manequim {clothingSize} Sapato {shoeSize}
                  </Text>
                  <Text style={{ fontSize: 8, color: "#999999", marginTop: 4 }}>@agenciavogue - 011-3032-9797</Text>
                </View>
              </View>
            </>
          )}

          <PdfImage src={logoComposite} style={pdfStyles.absoluteLogo} />
        </Page>
      </Document>
    );
  }, [data, talent, isAdultLayout]);
};
